import { type ErrorComponentProps, useRouter } from "@tanstack/react-router";
import {
  AlertCircle,
  AlertTriangle,
  Clock,
  FileQuestion,
  LogIn,
  ServerCrash,
  ShieldX,
  WifiOff,
} from "lucide-react";
import { useEffect, useMemo } from "react";

import { useAuthActions } from "~/integrations/vortex-auth/hooks";
import { type ErrorType, parseError } from "~/lib/errors";
import { logger, PortalEvents } from "~/lib/logger";

import { Button } from "./ui/button";

const errorIcons: Record<ErrorType, React.ReactNode> = {
  auth: <LogIn className="text-destructive size-12" />,
  permission: <ShieldX className="text-destructive size-12" />,
  network: <WifiOff className="text-warning size-12" />,
  not_found: <FileQuestion className="text-muted-foreground size-12" />,
  rate_limit: <Clock className="text-warning size-12" />,
  validation: <AlertTriangle className="text-warning size-12" />,
  server: <ServerCrash className="text-destructive size-12" />,
  generic: <AlertCircle className="text-destructive size-12" />,
};

const errorTitles: Record<ErrorType, string> = {
  auth: "Session Expired",
  permission: "Access Denied",
  network: "Connection Lost",
  not_found: "Not Found",
  rate_limit: "Too Many Requests",
  validation: "Invalid Input",
  server: "Server Error",
  generic: "Something Went Wrong",
};

export function DefaultCatchBoundary({ error, reset }: ErrorComponentProps) {
  const router = useRouter();
  const { signOut } = useAuthActions();

  const parsedError = useMemo(() => parseError(error), [error]);

  // Log error boundary trigger
  useEffect(() => {
    logger.exception(PortalEvents.ERROR_BOUNDARY_TRIGGERED, error, {
      error_type: parsedError.type,
      suggested_action: parsedError.action,
      current_path: location.pathname,
    });
  }, [error, parsedError]);

  const handleAction = async () => {
    switch (parsedError.action) {
      case "sign_in":
        await signOut();
        void router.navigate({ to: "/sign-in" });
        break;
      case "go_back":
        router.history.back();
        break;
      case "go_home":
        await router.navigate({ to: "/" });
        break;
      default:
        reset();
        break;
    }
  };

  return (
    <div className="flex min-h-dvh flex-col items-center justify-center p-4">
      <div className="w-full max-w-md space-y-6 text-center">
        {/* Icon */}
        <div className="flex justify-center">
          {errorIcons[parsedError.type]}
        </div>

        {/* Title & Message */}
        <div className="space-y-2">
          <h1 className="text-4xl font-bold tracking-tight text-balance">
            {errorTitles[parsedError.type]}
          </h1>
          <p className="text-muted-foreground">{parsedError.message}</p>
        </div>

        {/* Technical details (only in development or for generic/server errors) */}
        {error instanceof Error &&
          (parsedError.type === "generic" || parsedError.type === "server") && (
            <details className="border-destructive/50 bg-destructive/10 rounded-lg border p-4 text-left">
              <summary className="text-destructive cursor-pointer font-medium">
                Technical Details
              </summary>
              <p className="text-destructive mt-2 font-mono text-sm">
                {error.message}
              </p>
              {error.stack && (
                <pre className="text-muted-foreground mt-2 max-h-32 overflow-auto text-xs">
                  {error.stack}
                </pre>
              )}
            </details>
          )}

        {/* Actions */}
        <div className="flex flex-col gap-2 sm:flex-row sm:justify-center">
          <Button onClick={handleAction} type="button">
            {parsedError.actionText ?? "Try Again"}
          </Button>
          {parsedError.action !== "go_home" && (
            <Button
              onClick={async () => {
                await router.navigate({ to: "/" });
              }}
              type="button"
              variant="outline"
            >
              Go Home
            </Button>
          )}
        </div>

        {/* Help text for specific error types */}
        {parsedError.type === "network" && (
          <p className="text-muted-foreground text-sm">
            Check your internet connection and try again. If the problem
            persists, our servers may be temporarily unavailable.
          </p>
        )}
        {parsedError.type === "permission" && (
          <p className="text-muted-foreground text-sm">
            If you believe you should have access, contact your organization
            administrator.
          </p>
        )}
      </div>
    </div>
  );
}
